// src/toolkitServices/diamondAttributeApi.js
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const diamondAttributeApi = createApi({
  reducerPath: "diamondAttributeApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_BASE_URL,
    credentials: "include",
  }),
  tagTypes: ["DiamondAttribute", "DiamondAttributeGrouped"],
  endpoints: (builder) => ({
    // list with filters & paging
    getDiamondAttributes: builder.query({
      query: (params = {}) => {
        const usp = new URLSearchParams();
        Object.entries(params).forEach(([k, v]) => {
          if (v === undefined || v === null || v === "") return;
          usp.set(k, v);
        });
        const qs = usp.toString();
        return `/diamond-attributes${qs ? `?${qs}` : ""}`;
      },
      providesTags: (res) =>
        res?.data
          ? [
              ...res.data.map((r) => ({ type: "DiamondAttribute", id: r._id })),
              { type: "DiamondAttribute", id: "LIST" },
            ]
          : [{ type: "DiamondAttribute", id: "LIST" }],
    }),

    // grouped active for dropdowns (shape/color/clarity)
    getAllGroupedDiamondAttributes: builder.query({
      query: () => `/diamond-attributes/all`,
      providesTags: [{ type: "DiamondAttributeGrouped", id: "ALL" }],
    }),

    createDiamondAttribute: builder.mutation({
      query: (body) => ({
        url: `/diamond-attributes/create`,
        method: "POST",
        body,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const created = res?.data || res;

          const subs =
            diamondAttributeApi.util.selectInvalidatedBy(getState(), [
              { type: "DiamondAttribute", id: "LIST" },
            ]) || [];

          for (const { endpointName, originalArgs } of subs) {
            if (endpointName !== "getDiamondAttributes") continue;

            const { type, activeOnly, page, limit } = originalArgs || {};
            const matchesType = !type || type === created.type;
            const matchesActive =
              !activeOnly || activeOnly === "" ? true : created.isActive;

            if (matchesType && matchesActive && (page ?? 1) === 1) {
              dispatch(
                diamondAttributeApi.util.updateQueryData(
                  "getDiamondAttributes",
                  originalArgs,
                  (draft) => {
                    draft.data = draft.data || [];
                    if (!draft.data.find((x) => x._id === created._id)) {
                      draft.data.unshift(created);
                      if (limit && draft.data.length > limit) draft.data.pop();
                      draft.total = (draft.total || 0) + 1;
                    }
                  }
                )
              );
            }
          }

          // patch grouped
          dispatch(
            diamondAttributeApi.util.updateQueryData(
              "getAllGroupedDiamondAttributes",
              undefined,
              (draft) => {
                if (!draft || !created?.type) return;
                const key = created.type; // "shape" | "color" | "clarity"
                if (!draft[key]) return;
                const arr = draft[key];
                const idx = arr.findIndex((x) => x._id === created._id);
                if (created.isActive) {
                  if (idx === -1) arr.unshift(created);
                  else arr[idx] = created;
                } else if (idx !== -1) {
                  arr.splice(idx, 1);
                }
              }
            )
          );
        } catch {}
      },
    }),

    updateDiamondAttribute: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `/diamond-attributes/${id}`,
        method: "PUT",
        body,
      }),
      async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled, getState }) {
        try {
          const { data: res } = await queryFulfilled;
          const updated = res?.data || { _id: id, ...patch };

          const subs =
            diamondAttributeApi.util.selectInvalidatedBy(getState(), [
              { type: "DiamondAttribute", id: "LIST" },
            ]) || [];

          for (const { endpointName, originalArgs } of subs) {
            if (endpointName !== "getDiamondAttributes") continue;

            dispatch(
              diamondAttributeApi.util.updateQueryData(
                "getDiamondAttributes",
                originalArgs,
                (draft) => {
                  if (!draft?.data) return;

                  const { type, activeOnly, page, limit } = originalArgs || {};
                  const matchesType = !type || type === updated.type;
                  const matchesActive =
                    !activeOnly || activeOnly === "" ? true : updated.isActive;

                  const idx = draft.data.findIndex((x) => x._id === id);

                  if (idx !== -1) {
                    if (matchesType && matchesActive) {
                      draft.data[idx] = { ...draft.data[idx], ...updated };
                    } else {
                      draft.data.splice(idx, 1);
                      draft.total = Math.max(0, (draft.total || 0) - 1);
                    }
                  } else if (matchesType && matchesActive && (page ?? 1) === 1) {
                    draft.data.unshift(updated);
                    if (limit && draft.data.length > limit) draft.data.pop();
                    draft.total = (draft.total || 0) + 1;
                  }
                }
              )
            );
          }

          // patch grouped
          dispatch(
            diamondAttributeApi.util.updateQueryData(
              "getAllGroupedDiamondAttributes",
              undefined,
              (draft) => {
                if (!draft || !updated?.type) return;
                const key = updated.type; // shape/color/clarity
                if (!draft[key]) return;
                const arr = draft[key];
                const i = arr.findIndex((x) => x._id === updated._id);
                if (updated.isActive) {
                  if (i === -1) arr.unshift(updated);
                  else arr[i] = { ...arr[i], ...updated };
                } else if (i !== -1) {
                  arr.splice(i, 1);
                }
              }
            )
          );
        } catch {}
      },
    }),

    deleteDiamondAttribute: builder.mutation({
      query: (id) => ({
        url: `/diamond-attributes/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const patches = [];
        const subs =
          diamondAttributeApi.util.selectInvalidatedBy(getState(), [
            { type: "DiamondAttribute", id: "LIST" },
          ]) || [];

        for (const { endpointName, originalArgs } of subs) {
          if (endpointName !== "getDiamondAttributes") continue;

          const patchResult = dispatch(
            diamondAttributeApi.util.updateQueryData(
              "getDiamondAttributes",
              originalArgs,
              (draft) => {
                if (!draft?.data) return;
                const idx = draft.data.findIndex((x) => x._id === id);
                if (idx !== -1) {
                  draft.data.splice(idx, 1);
                  draft.total = Math.max(0, (draft.total || 0) - 1);
                }
              }
            )
          );
          patches.push(patchResult);
        }

        try {
          await queryFulfilled;
          // remove from grouped
          dispatch(
            diamondAttributeApi.util.updateQueryData(
              "getAllGroupedDiamondAttributes",
              undefined,
              (draft) => {
                if (!draft) return;
                ["shape", "color", "clarity"].forEach((k) => {
                  if (!draft[k]) return;
                  const i = draft[k].findIndex((x) => x._id === id);
                  if (i !== -1) draft[k].splice(i, 1);
                });
              }
            )
          );
        } catch {
          patches.forEach((p) => p.undo && p.undo());
        }
      },
    }),
  }),
});

export const {
  useGetDiamondAttributesQuery,
  useGetAllGroupedDiamondAttributesQuery,
  useCreateDiamondAttributeMutation,
  useUpdateDiamondAttributeMutation,
  useDeleteDiamondAttributeMutation,
} = diamondAttributeApi;

export default diamondAttributeApi;
